import React, { useEffect, useRef, useState } from 'react';
import { BpsAdjustment } from '../types/commission';
import { formatSignedBps } from '../utils/commission';
interface PeriodAdjustmentCardProps {
  current: BpsAdjustment | null;
  loanCount: number;
  onApply: (value: BpsAdjustment | null) => void;
}
const SAVE_DELAY = 700;

/** Admin-only, period-wide BPS deduction or adjustment applied to every loan. */
export function PeriodAdjustmentCard({
  current,
  loanCount,
  onApply
}: PeriodAdjustmentCardProps) {
  const [bps, setBps] = useState(current ? String(current.bps) : '');
  const [reason, setReason] = useState(current?.reason ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const timer = useRef<number>();
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const summary = current ? `${formatSignedBps(current.bps)} currently applied to every loan in this period — “${current.reason}”` : null;
  function handleSave() {
    setError(null);
    setSuccess(null);
    const value = bps.trim() === '' ? 0 : Number(bps);
    if (Number.isNaN(value)) {
      setError('Enter a BPS value. Use a negative number for a deduction.');
      return;
    }
    if (value !== 0 && reason.trim().length === 0) {
      setError('Add a reason so the adjustment is auditable.');
      return;
    }
    setSaving(true);
    timer.current = window.setTimeout(() => {
      setSaving(false);
      onApply(value === 0 ? null : {
        bps: value,
        reason: reason.trim()
      });
      setSuccess(value === 0 ? 'Adjustment cleared for all loans in this period.' : `Applied ${formatSignedBps(value)} to ${loanCount} ${loanCount === 1 ? 'loan' : 'loans'} in this period.`);
    }, SAVE_DELAY);
  }
  return <section aria-labelledby="period-bps-adjustment-heading" className="grid gap-6 rounded-xl border border-amber-200 bg-amber-50/60 p-6 shadow-card md:grid-cols-[1fr_1.4fr]" data-id="element-386" data-mp="ns4570acya1n">
      <div data-id="element-387" data-mp="2ug37n1hha6tm">
        <p className="text-xs font-semibold uppercase tracking-widest text-amber-700" data-id="element-388" data-mp="2pexa8qcvhvdr">Admin adjustment</p>
        <h2 id="period-bps-adjustment-heading" className="mt-1.5 text-base font-bold text-slate-900" data-id="element-389" data-mp="2knxehofhpjcu">
          BPS deduction/adjustment
        </h2>
        <p className="mt-1 text-sm text-slate-600" data-id="element-390" data-mp="2fokt7849okcd">
          {summary ?? 'Applies to every loan in this pay period. Loans with an individual BPS override keep their override.'}
        </p>
      </div>

      <div className="space-y-3" data-id="element-391" data-mp="26mur98cre30c">
        {error ? <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700" data-id="element-392" data-mp="21ot70k3ljxna">
            {error}
          </p> : null}
        {success ? <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800" data-id="element-393" data-mp="3w3vu6b56b3n1">
            {success}
          </p> : null}

        <div className="grid gap-3 sm:grid-cols-[160px_1fr]" data-id="element-394" data-mp="3f458ls3w8f2">
          <label className="block" data-id="element-395" data-mp="7h8yvkik151p">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-396" data-mp="7g66t99hwwk7">BPS deduction/adjustment</span>
            <input type="number" step="0.1" value={bps} onChange={event => setBps(event.target.value)} aria-label="BPS deduction or adjustment" placeholder="e.g. -2.5" className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm tabular-nums text-slate-900" data-id="element-397" data-mp="c8z97qhhrst4" />
          </label>
          <label className="block" data-id="element-398" data-mp="3ppgiavfp9t6i">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-399" data-mp="3umgor7ggoxy5">Reason</span>
            <textarea rows={2} value={reason} onChange={event => setReason(event.target.value)} placeholder="Why is this adjustment being applied?" aria-label="BPS deduction or adjustment reason" className="mt-1.5 w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400" data-id="element-400" data-mp="14xo2x4g7wvuu" />
          </label>
        </div>

        <div className="flex justify-end" data-id="element-401" data-mp="2muhr6drcf4e7">
          <button type="button" onClick={handleSave} disabled={saving} className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-800 disabled:cursor-not-allowed disabled:bg-brand-300" data-id="element-402" data-mp="2mtq6hxzaar2p">
            {saving ? 'Applying…' : 'Apply to all loans in this period'}
          </button>
        </div>
      </div>
    </section>;
}